/**
 * Mixer session state - the live/optimistic per-strip state shared across
 * every Mixer item, keyed by ABSOLUTE track index (bankOffset + strip) so
 * multiple mixer items mirroring the same bank stay in lock-step.
 *
 * Same pattern as `transpose/session.ts`: a thin store over pure helpers,
 * persisted to localStorage for reload continuity. Strip state is NOT stored
 * on the MixerItem (that's config only) - it is Live's truth (when the
 * feedback half is live) or the operator's last local action (optimistic,
 * before feedback exists). `known` distinguishes the two.
 *
 * v0.1 has no feedback yet, so only the local setters fire; the `applyMixer*`
 * appliers are wired to the status poller when the OPBridge MixerState feed
 * lands (MIXER-PAGE-DESIGN.md section 6). See also [[project_snapshot_primitive_designed]]
 * for the sibling send-cache pattern.
 *
 * A plain signal-of-Record is fine for the v0.1 8-strip bank (8 cheap DOM
 * strips). If paging to 60 tracks ever makes per-strip re-render cost matter,
 * switch to `createStore` from solid-js/store for fine-grained updates.
 */

import { createSignal } from 'solid-js';
import { sendMixerMute, sendMixerSolo, sendMixerArm, type FireResult } from '../opbridge/dispatch.ts';

export interface MixerStripState {
  /** Normalized volume 0..1 (Live's mixer_device.volume value). */
  volume: number;
  /** Track mute. */
  mute: boolean;
  /** Track name from the device's /name feedback. '' = unknown (render placeholder). */
  name: string;
  /** Output meter level 0..1 (only while the device METERS toggle is on). */
  meter: number;
  /** True once the feedback half has set this strip's truth. False = optimistic. */
  known: boolean;

  // ── v0.2 stub controls (solo / arm / pan / sends). Wired on the OPButtons
  //    side now (control dispatch + optimistic state), but the device
  //    (OP-AbletonMixer v0.1) does NOT speak these verbs yet — they land two-way
  //    when the device v0.2 + OPBridge feedback fields ship (see
  //    OPApps/opbuttons-opm4lbuilder.md). Until then these are optimistic-only.
  /** Track solo. */
  solo: boolean;
  /** Track record-arm. */
  arm: boolean;
  /** Pan, -1 (hard left) .. +1 (hard right), 0 = center (Live's panning value). */
  pan: number;
  /** Per-send levels 0..1, indexed by send slot (A=0, B=1, ...). */
  sends: number[];
  /** Live send-slot count for this track, from the device's `/sendcount`
   *  feedback (= the set's return-track count). Undefined until feedback
   *  arrives — the strip falls back to the item's configured `sendCount`. */
  sendCount?: number;
}

const STRIPS_KEY = 'opbuttons.mixer.strips.v1';

/** Reserved strip key for the master track (no per-track index; volume + pan
 *  only). The Mixer renders it in master mode; dispatch routes idx<0 to the
 *  `set_master_*` verbs; the poller applies device `/master_*` feedback here. */
export const MASTER_IDX = -1;

/** Session/render index base for return tracks (`RETURN_BASE + i` = return i).
 *  Kept well clear of regular-track indices (0..N) and the master key (-1).
 *  Dispatch routes idx>=RETURN_BASE to the `set_return_*` verbs. */
export const RETURN_BASE = 2000;

const DEFAULT_STRIP: MixerStripState = {
  volume: 0.85, // rest at unity so an un-touched strip reads 0 dB, not silent
  mute: false,
  name: '',
  meter: 0,
  known: false,
  solo: false,
  arm: false,
  pan: 0, // center
  sends: [],
};

type StripMap = Record<number, MixerStripState>;

function loadStrips(): StripMap {
  if (typeof localStorage === 'undefined') return {};
  const raw = localStorage.getItem(STRIPS_KEY);
  if (raw === null) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') return parsed as StripMap;
  } catch { /* corrupt -> start fresh */ }
  return {};
}

function persistStrips(map: StripMap): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(STRIPS_KEY, JSON.stringify(map)); } catch { /* quota / private mode */ }
}

const [stripsSig, setStripsSig] = createSignal<StripMap>(loadStrips());

// ─── Echo suppression (DEC-467) ──────────────────────────────────────────
// A strip the operator is actively dragging must ignore incoming `/volume`
// feedback for a short window so the device's echo of our own move doesn't
// fight the finger. The status poller checks `volumeEchoSuppressed(idx)` before
// applying a volume; mute/name/meter are never suppressed (mute is one-shot).
const ECHO_WINDOW_MS = 250;
const lastLocalVolumeAt: Record<number, number> = {};
// Pan is continuous like volume, so a dragged pan must also ignore its own echo
// for a short window. Keyed `idx * 1000 + sendIdx` for sends (small-N safe).
const lastLocalPanAt: Record<number, number> = {};
const lastLocalSendAt: Record<number, number> = {};

/** True while `idx` was locally set within the echo window — the poller should
 *  NOT overwrite its volume with device feedback yet. */
export function volumeEchoSuppressed(idx: number): boolean {
  const t = lastLocalVolumeAt[idx];
  return t !== undefined && Date.now() - t < ECHO_WINDOW_MS;
}

/** Echo guard for pan (same contract as {@link volumeEchoSuppressed}). */
export function panEchoSuppressed(idx: number): boolean {
  const t = lastLocalPanAt[idx];
  return t !== undefined && Date.now() - t < ECHO_WINDOW_MS;
}

/** Echo guard for one send slot (keyed idx+sendIdx). */
export function sendEchoSuppressed(idx: number, sendIdx: number): boolean {
  const t = lastLocalSendAt[idx * 1000 + sendIdx];
  return t !== undefined && Date.now() - t < ECHO_WINDOW_MS;
}

/** Reactive read of one strip's state (defaults for an untouched strip).
 *  Call inside a tracking scope (a JSX binding / memo) - it reads the strips
 *  signal, so it re-runs when any strip changes. Merges DEFAULT_STRIP so a strip
 *  persisted before the solo/arm/pan/sends fields existed still reads sane
 *  defaults for them (forward-compat with older localStorage). */
export function stripState(idx: number): MixerStripState {
  const s = stripsSig()[idx];
  return s ? { ...DEFAULT_STRIP, ...s } : DEFAULT_STRIP;
}

function patchStrip(idx: number, patch: Partial<MixerStripState>): void {
  const cur = stripsSig()[idx] ?? DEFAULT_STRIP;
  const next: StripMap = { ...stripsSig(), [idx]: { ...cur, ...patch } };
  setStripsSig(next);
  persistStrips(next);
}

// ─── Local (optimistic) setters - used by the Mixer component NOW ─────────
// These update the visible strip immediately; they do NOT set `known` (that's
// the feedback half's job) - an optimistic value is what the operator set, not
// confirmed Live truth. Volume is state-ONLY here so the component can render
// every drag frame while dispatching through its own per-strip rate guard
// (MIXER-PAGE-DESIGN.md section 4.5); mute is a one-shot so it dispatches here.

/** Update a strip's volume optimistically (state only, no dispatch). The
 *  component owns the guarded `sendMixerVolume` so a fast drag can't back up
 *  the wire on a slow bridge. */
export function setStripVolumeOptimistic(idx: number, v: number): void {
  lastLocalVolumeAt[idx] = Date.now();   // arm echo-suppression for this strip
  patchStrip(idx, { volume: clamp01(v) });
}

/** Toggle/set a strip's mute locally + dispatch `set_mute` to the device
 *  (one-shot, no throttle needed). */
export function setStripMuteLocal(idx: number, muted: boolean, target = ''): Promise<FireResult[]> {
  patchStrip(idx, { mute: muted });
  return sendMixerMute(idx, muted, target);
}

/** Toggle/set a strip's solo locally + dispatch `set_solo` (one-shot). Stub —
 *  the device v0.1 ignores this verb until v0.2 (see file header). */
export function setStripSoloLocal(idx: number, solo: boolean, target = ''): Promise<FireResult[]> {
  patchStrip(idx, { solo });
  return sendMixerSolo(idx, solo, target);
}

/** Toggle/set a strip's record-arm locally + dispatch `set_arm` (one-shot). Stub. */
export function setStripArmLocal(idx: number, arm: boolean, target = ''): Promise<FireResult[]> {
  patchStrip(idx, { arm });
  return sendMixerArm(idx, arm, target);
}

/** Update a strip's pan optimistically (state only, no dispatch — the component
 *  owns the guarded `sendMixerPan` so a fast drag can't back up the wire, same
 *  as volume). Value clamped -1..1. Stub. */
export function setStripPanOptimistic(idx: number, pan: number): void {
  lastLocalPanAt[idx] = Date.now();
  patchStrip(idx, { pan: clampBipolar(pan) });
}

/** Update one send slot optimistically (state only, no dispatch — component owns
 *  the guarded `sendMixerSend`). Value clamped 0..1. Stub. */
export function setStripSendOptimistic(idx: number, sendIdx: number, v: number): void {
  lastLocalSendAt[idx * 1000 + sendIdx] = Date.now();
  const cur = (stripsSig()[idx] ?? DEFAULT_STRIP).sends ?? [];
  const next = cur.slice();
  next[sendIdx] = clamp01(v);
  patchStrip(idx, { sends: next });
}

// ─── Feedback appliers - wired to the status poller LATER ─────────────────
// The Live -> iPad half calls these from `mixer/statusPoll.ts` once OPBridge
// forwards the device feed. They set `known: true` (this is confirmed truth).
// Echo reconciliation (don't fight a finger mid-drag) lives in the poller/
// component, not here (MIXER-PAGE-DESIGN.md section 6).

export function applyMixerVolume(idx: number, v: number): void {
  patchStrip(idx, { volume: clamp01(v), known: true });
}
export function applyMixerMute(idx: number, muted: boolean): void {
  patchStrip(idx, { mute: muted, known: true });
}
export function applyMixerName(idx: number, name: string): void {
  // Be defensive about the Max symbol-quoting artifact (names can arrive
  // wrapped in literal double-quotes, e.g. `"1-Analog Piano"`). OPBridge strips
  // them on newer builds; strip here too so an older daemon doesn't leak quotes.
  const clean = name.replace(/^"(.*)"$/, '$1');
  patchStrip(idx, { name: clean, known: true });
}
export function applyMixerMeter(idx: number, v: number): void {
  patchStrip(idx, { meter: clamp01(v) });
}
export function applyMixerSolo(idx: number, solo: boolean): void {
  patchStrip(idx, { solo, known: true });
}
export function applyMixerArm(idx: number, arm: boolean): void {
  patchStrip(idx, { arm, known: true });
}
export function applyMixerPan(idx: number, pan: number): void {
  patchStrip(idx, { pan: clampBipolar(pan), known: true });
}
export function applyMixerSend(idx: number, sendIdx: number, v: number): void {
  const cur = (stripsSig()[idx] ?? DEFAULT_STRIP).sends ?? [];
  const next = cur.slice();
  next[sendIdx] = clamp01(v);
  patchStrip(idx, { sends: next, known: true });
}
/** Set a strip's live send-slot count (device `/sendcount` feedback). */
export function applyMixerSendCount(idx: number, n: number): void {
  patchStrip(idx, { sendCount: Math.max(0, Math.min(12, Math.round(n))), known: true });
}

/** Display name for a strip: the live name, or a `TR{n}` placeholder. */
export function stripLabel(idx: number, name: string): string {
  return name.trim() !== '' ? name : `TR${idx + 1}`;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function clampBipolar(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(-1, Math.min(1, n));
}
